import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, alpha } from '@mui/material';
import {
  CheckCircle,
  WatchLater,
  ShowChart,
  Assessment,
} from '@mui/icons-material';

// ── Stat tile ─────────────────────────────────
const StatTile = ({ label, value, sub, color, Icon, children }) => (
  <Box sx={{
    flex: 1, minWidth: 0,
    position: 'relative', overflow: 'hidden',
    borderRadius: 2,
    border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff',
    p: '12px 14px',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: alpha(color, 0.35),
      boxShadow: `0 2px 12px ${alpha(color, 0.1)}`,
      transform: 'translateY(-1px)',
    },
  }}>
    {/* Background accent */}
    <Box sx={{
      position: 'absolute', top: 0, right: 0,
      width: 60, height: 60,
      background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)`,
    }} />

    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
      {/* Icon badge */}
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 2px 6px ${alpha(color, 0.3)}`,
      }}>
        <Icon sx={{ fontSize: 18, color: '#fff' }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.4,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 900, color, lineHeight: 1, fontFamily: 'monospace',
        }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.3, lineHeight: 1.3 }}>
            {sub}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  </Box>
);

const StatsCards = ({ results }) => {
  const { t } = useTranslation();

  const triggerCount = results.results.filter((r) => r.trigger_flag || r.early_trigger_flag).length;
  const watchCount = results.results.filter((r) => r.setup_flag && !r.trigger_flag && !r.early_trigger_flag).length;
  const totalScanned = results.total_scanned || 0;
  const totalSignals = results.total_signals || 0;

  const watchList = results.results.filter(r => r.setup_flag && !r.trigger_flag && !r.early_trigger_flag);
  const goodSetup = watchList.filter(r => r.setup_score >= 90).length;
  const avgSetupScore = results.results.length > 0
    ? results.results.reduce((sum, r) => sum + (r.setup_score || 0), 0) / results.results.length
    : 0;

  const topTriggers = results.results
    .filter(r => r.trigger_flag || r.early_trigger_flag)
    .sort((a, b) => b.setup_score - a.setup_score)
    .slice(0, 3);

  const qualityColor = avgSetupScore >= 85 ? '#10b981' : avgSetupScore >= 70 ? '#f59e0b' : '#94a3b8';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
      {/* Total Scanned */}
      <StatTile
        label={t('earlyPivot.stats.totalScanned')}
        value={totalScanned}
        sub={`${totalSignals} signals · ${totalScanned > 0 ? ((totalSignals / totalScanned) * 100).toFixed(0) : 0}% hit rate`}
        color="#3b82f6"
        Icon={Assessment}
      />

      {/* Buy Ready */}
      <StatTile
        label={t('earlyPivot.stats.buyReady')}
        value={triggerCount}
        sub={triggerCount > 0 && topTriggers.length > 0
          ? `Top: ${topTriggers.map(r => r.ticker).join(', ')}`
          : t('earlyPivot.stats.buyReadyNone')
        }
        color={triggerCount > 0 ? '#10b981' : '#94a3b8'}
        Icon={CheckCircle}
      />

      {/* Watching */}
      <StatTile
        label={t('earlyPivot.stats.watching')}
        value={watchCount}
        sub={watchCount > 0 ? `${goodSetup} excellent · ${watchCount - goodSetup} normal` : t('earlyPivot.stats.watchingNone')}
        color="#f59e0b"
        Icon={WatchLater}
      />

      {/* Quality Score */}
      <StatTile
        label={t('earlyPivot.stats.quality')}
        value={avgSetupScore.toFixed(0)}
        color={qualityColor}
        Icon={ShowChart}
      >
        {/* Score bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <Box sx={{ flex: 1, height: 4, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{
              height: '100%', borderRadius: 2, transition: 'width 0.4s',
              width: `${avgSetupScore}%`, bgcolor: qualityColor,
            }} />
          </Box>
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: qualityColor, fontFamily: 'monospace' }}>
            /100
          </Typography>
        </Box>
      </StatTile>
    </Box>
  );
};

export default StatsCards;
